import config from '../views/config';
import { getAuthToken, getCurrentProvider } from './providerApi';

export const PENDING_PROMO_CODE_KEY = 'pendingProviderPromoCode';
export const PROMO_SUCCESS_MESSAGE =
  "Congratulations! You've activated a complimentary 1-month Yogamatt subscription. Enjoy the perks of being a Yogamatter!";

export function extractPromoCodeFromUrl(url) {
  if (!url) return null;
  try {
    const decoded = decodeURIComponent(url);
    const match = decoded.match(/promo\/([^/?#]+)/i) || decoded.match(/[?&]code=([^&]+)/i);
    if (!match) return null;
    return decodeURIComponent(match[1]).trim();
  } catch (_error) {
    return null;
  }
}

export function savePendingPromoCode(code) {
  if (!code) return;
  localStorage.setItem(PENDING_PROMO_CODE_KEY, code.trim());
}

export function peekPendingPromoCode() {
  return localStorage.getItem(PENDING_PROMO_CODE_KEY);
}

export function clearPendingPromoCode() {
  localStorage.removeItem(PENDING_PROMO_CODE_KEY);
}

export async function fetchProviderSubscription() {
  try {
    const provider = await getCurrentProvider();
    return provider?.subscription || null;
  } catch (error) {
    console.error('Error loading teacher subscription:', error);
    return null;
  }
}

export async function syncProviderSubscription() {
  const token = getAuthToken();
  if (!token) return null;

  const response = await fetch(`${config.apiUrl}/providers/subscription/sync`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) return null;
  const data = await response.json();
  return data?.subscription || null;
}

export async function redeemPromoCode(code) {
  const token = getAuthToken();
  if (!token) {
    return { ok: false, message: 'Please sign in again.' };
  }

  const response = await fetch(`${config.apiUrl}/promo/redeem`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, code }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, message: data?.message || 'Unable to redeem code.' };
  }
  return { ok: true, message: data?.message, subscription: data?.subscription };
}

let redeemInFlight = false;

export async function redeemPendingPromoCode() {
  if (redeemInFlight) return { handled: false };
  const code = peekPendingPromoCode();
  if (!code) return { handled: false };

  const token = getAuthToken();
  if (!token) {
    return { handled: false, needsLogin: true, code };
  }

  redeemInFlight = true;
  try {
    const result = await redeemPromoCode(code);
    clearPendingPromoCode();
    return { handled: true, ...result };
  } catch (error) {
    console.error('Error redeeming promo code:', error);
    return { handled: true, ok: false, message: 'Please try again after logging in as a teacher.' };
  } finally {
    redeemInFlight = false;
  }
}

export function canPublishListings(subscription) {
  return Boolean(subscription?.listingEntitled);
}

export async function openPublishRoute(navigate, path, state) {
  try {
    const subscription = await fetchProviderSubscription();
    if (subscription && !subscription.listingEntitled) {
      navigate('/subscribe', {
        state: {
          nextPath: path,
          nextState: state,
        },
      });
      return false;
    }
  } catch (error) {
    console.error('Error checking listing subscription:', error);
  }

  navigate(path, { state });
  return true;
}

export function redirectIfSubscriptionRequired(navigate, response, nextPath, nextState) {
  if (response?.status !== 402) return false;
  navigate('/subscribe', {
    state: {
      nextPath,
      nextState,
    },
  });
  return true;
}
